import { useMemo, useState } from 'react';
import { WbPopover, WbSpinner, WbTooltip } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import FallbackImage from '../../../Assets/Vimdt/Images/fallback_vehicle.jpeg';
import FallbackImageNoBG from '../../../Assets/Vimdt/Images/fallback_vehicle_no_bg.jpeg';
import { useImageContext } from '../../../Contexts/ImageContext';
import { _getImage } from '../../../Utils/Vimdt/apiCalls';
import { convertImage } from '../../../Utils/Vimdt/convertImage';
import './ImageCard.scss';

function ImageCard({ vin, imageId, url, title, imageType, background, isDownload }) {
	const ErrorImage = useMemo(
		() => (background ? FallbackImage : FallbackImageNoBG),
		[background]
	);
	const { t } = useTranslation();

	const { apikey, setImageList } = useImageContext();
	const [status, setStatus] = useState('loaded');

	const download = () => {
		if (!isDownload) return;
		const link = document.createElement('a');
		link.href = url;
		link.download = `${title}.${imageType}`;
		link.click();
	};

	const handleDownloadKeyDown = (event) => {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		download();
	};

	const reloadImage = () => {
		setStatus('loading');

		if (!imageId) {
			setImageList((prev) => ({
				...prev,
				[vin]: [...(prev[vin]?.filter((e) => e.title !== title) || []), { title, url }],
			}));
			setTimeout(() => setStatus('loaded'), 1000);
			return undefined;
		}

		return _getImage({ apikey, imageId })
			.then((imageData) => {
				if (!imageData) return;
				const image = convertImage(imageData.data, imageType);
				setImageList((prev) => ({
					...prev,
					[vin]: [
						...(prev[vin]?.filter((e) => e.imageId !== imageId) || []),
						{ imageId, title, ...image },
					],
				}));
				setTimeout(() => setStatus('loaded'), 1000);
			})
			.catch((err) => {
				setStatus('error');
				console.error(err);
			});
	};

	const needsReload = status !== 'loaded' || (!url && imageId);

	return (
		<div className="vimdt-image-card">
			{needsReload ? (
				<WbTooltip position="top">
					<button
						type="button"
						className="vimdt-image-card__reload"
						onClick={reloadImage}
						aria-label={t('vimdt.imagesTab.reloadToClick')}
					>
						{status === 'loading' ? <WbSpinner theme="dark" /> : <span aria-hidden="true">↻</span>}
					</button>
					<span slot="content">{t('vimdt.imagesTab.reloadToClick')}</span>
				</WbTooltip>
			) : (
				<WbPopover position="top">
					<div
						className="vimdt-image-card__image-wrapper"
						role="button"
						tabIndex={0}
						aria-label={title}
						onClick={download}
						onKeyDown={handleDownloadKeyDown}
					>
						<img
							className="vimdt-image-card__image"
							src={url || ErrorImage}
							onError={() => setStatus('error')}
							alt={title}
						/>
					</div>
					<div slot="content" className="vimdt-image-card__popover">
						<strong>{title}</strong>
						<img src={url || ErrorImage} alt={title} />
					</div>
				</WbPopover>
			)}
			<div className="vimdt-image-card__title">{title}</div>
		</div>
	);
}

export default ImageCard;
